import _ from 'underscore';
import React from 'react';
import { buildLegacyI18n } from '@bingads-webui/build-legacy-i18n';
import ValidationLegacyStrings from '@bingads-webui-clientcenter/validation-legacy-strings';
import { AddressUtils } from '@bingads-webui-clientcenter/address';
import { RadioGroup } from '@bingads-webui-react/primitive-validation';
import { withActivityContext } from '@bingads-webui-react/hoc-with-signal';
import { AddressSuggestionKey, DefaultAddressSelectionOptionId, MaxNumOfAddressesToShow } from './constants';
import type { AblAddress, AddressSuggestionsProps, AddressSuggestionsState } from './types';

interface RadioGroupProps {
  items: number[] | null;
  getItemValue: (selectedItem: number) => string;
  onChange: (selectedItem: number) => void;
  checked: number | undefined;
  renderItem: (selectedItem: number) => string;
  name: string;
  validationProps: {
    i18n: { getString: (text: string) => string };
    field: string;
  };
}

const TypedRadioGroup = RadioGroup as unknown as React.ComponentType<RadioGroupProps>;

class AddressSuggestionsUnwrapped extends React.Component<AddressSuggestionsProps, AddressSuggestionsState> {
  constructor(props: AddressSuggestionsProps) {
    super(props);
    this.state = {
      addressSelectedId: DefaultAddressSelectionOptionId,
    };
  }

  componentDidMount() {
    this.onAddressSelectionChange(DefaultAddressSelectionOptionId + 1);
  }

  onAddressSelectionChange = (selectedItem: number) => {
    const addressSelectedId = selectedItem - 1;
    let isOriginalAddress = false;
    let addressSelected = null;
    if ((addressSelectedId === MaxNumOfAddressesToShow || addressSelectedId === _.size(this.props.addressSuggestions))) {
      isOriginalAddress = true;
      addressSelected = this.props.originalAddress;
    } else {
      addressSelected = this.props.addressSuggestions[addressSelectedId];
    }
    this.setState({ addressSelectedId, addressSelected, isOriginalAddress }, () => this.props.onAddressChange(this.state));
  };

  render() {
    // Add the option for "Use what I entered"
    // Fix: for some reason, radios value has to start from 1 instead of 0. (Otherwise, the checked item will not be selected.)
    // HAX: increase the index by 1.
    const addressSuggestionsDisplayBlock = _.map(
      _.first(this.props.addressSuggestions, MaxNumOfAddressesToShow),
      (address: AblAddress) => AddressUtils.extendAddress(address).inlineFormat()
    );
    let addressOptionLabels = addressSuggestionsDisplayBlock;
    let addressOptionValues = addressOptionLabels ? addressOptionLabels.map((_addressLabel: string, index: number) => (index + 1)) : null;
    const originalOptionLabel = [this.props.i18n.getString(_TL_('Use what I entered'))];
    if (this.props.allowSaveInvalidAddress) {
      if (_.size(addressOptionLabels)) {
        addressOptionValues = Array.prototype.concat(addressOptionValues, _.size(addressOptionLabels) + 1);
        addressOptionLabels = Array.prototype.concat(addressOptionLabels, originalOptionLabel);
      } else {
        addressOptionValues = [1];
        addressOptionLabels = originalOptionLabel;
      }
    }

    const validationI18n = buildLegacyI18n(
      this.props.i18n,
      ValidationLegacyStrings as unknown as ReadonlyArray<Record<string, string>>
    );

    return (
      <div id={this.props.id} className="address-suggestion-radio-group">
        <TypedRadioGroup
          items={addressOptionValues}
          getItemValue={(selectedItem: number) => selectedItem as unknown as string}
          onChange={(selectedItem: number) => {
            if (this.props.activity) {
              this.props.activity.signal(`select from suggestions: ${selectedItem} out of ${_.size(addressOptionValues)}`, 'address-suggestions');
            }
            this.onAddressSelectionChange(selectedItem);
          }}
          checked={_.findWhere(addressOptionValues, this.state.addressSelectedId)}
          renderItem={(selectedItem: number) => addressOptionLabels[selectedItem - 1]}
          name="address-suggestions-radio-group"
          validationProps={{ i18n: validationI18n, field: AddressSuggestionKey }}
        />
      </div>
    );
  }
}

(AddressSuggestionsUnwrapped as typeof AddressSuggestionsUnwrapped & {
  defaultProps: Partial<AddressSuggestionsProps>;
}).defaultProps = {
  addressSuggestions: [],
  originalAddress: {},
  allowSaveInvalidAddress: true,
  activity: null,
};


export const AddressSuggestions = withActivityContext(AddressSuggestionsUnwrapped as React.ComponentType<AddressSuggestionsProps & {
  activity: NonNullable<AddressSuggestionsProps['activity']>;
}>);
